import { Reveal, SectionHead, GoldBtn, PageHero, Particles } from "@/components/Shared";
import { PITCH_ROUNDS, PITCH_FEATURES } from "@/data/content";

const JOURNEY = ["Application", "Screening", "Thematic Rounds", "Semi-Finals", "Grand Finale"];

export default function PitchYatra() {
    return (
        <div data-testid="pitchyatra-page">
            <section className="relative pt-40 pb-24 md:pt-52 md:pb-36 overflow-hidden noise text-center"
                style={{ background: "linear-gradient(165deg, #2A0810 0%, #1A060C 55%, #140409 100%)" }}
                data-testid="pitchyatra-hero">
                <Particles density={55} />
                <div className="absolute inset-0 jaali opacity-25 pointer-events-none" />
                <div className="container-x relative">
                    <Reveal>
                        <span className="eyebrow">The Showdown</span>
                        <h1 className="font-display font-medium text-5xl sm:text-7xl lg:text-8xl leading-none mt-8">
                            PITCH<span className="gold-text italic">YATRA</span>
                        </h1>
                        <p className="font-display text-3xl sm:text-4xl gold-text mt-4 tracking-wide">2027</p>
                        <p className="mt-6 text-[0.64rem] sm:text-xs tracking-[0.42em] uppercase text-muted-luxe">
                            India's Ultimate Startup Showdown
                        </p>
                        <p className="mt-8 max-w-2xl mx-auto text-muted-luxe text-base md:text-lg leading-relaxed">
                            A live pitch marathon where 120 startups battle across five thematic rounds in three
                            days — for funding, visibility and recognition on the grandest stage of the bazaar.
                        </p>
                    </Reveal>
                    <Reveal delay={150}>
                        <div className="mt-14 flex items-center justify-center gap-10 sm:gap-20">
                            {[["120", "Startups"], ["5", "Rounds"], ["3", "Days"]].map(([v, l]) => (
                                <div key={l}>
                                    <div className="font-display text-5xl sm:text-7xl gold-text font-semibold">{v}</div>
                                    <div className="mt-2 text-[0.58rem] tracking-[0.34em] uppercase text-muted-luxe">{l}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-14">
                            <GoldBtn to="/register?type=startup" testid="pitchyatra-enter-btn">Enter PitchYatra</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="container-x">
                    <SectionHead eyebrow="The Rounds" title={<>Five Arenas. <span className="gold-text">One Champion's Path.</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {PITCH_ROUNDS.map((r, i) => (
                            <Reveal key={r.name} delay={i * 80}>
                                <div className="card-luxe tilt p-7 h-full text-center" data-testid={`pitch-round-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <div className="card-glow" />
                                    <div className="w-14 h-16 arch-top border border-[var(--line)] mx-auto flex items-center justify-center">
                                        <r.icon size={22} strokeWidth={1.2} className="text-[var(--gold)]" />
                                    </div>
                                    <span className="block mt-5 font-display text-sm text-[var(--gold)]/70">Round 0{i + 1}</span>
                                    <h3 className="font-display text-xl mt-2 text-[var(--ivory)]">{r.name}</h3>
                                    <p className="mt-3 text-xs text-muted-luxe leading-relaxed">{r.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60 noise relative overflow-hidden">
                <div className="container-x">
                    <SectionHead eyebrow="The Yatra" title={<>The Journey to <span className="gold-text">the Finale</span></>} />
                    <Reveal delay={140}>
                        <div className="mt-16 flex flex-col md:flex-row items-stretch justify-center gap-4 max-w-5xl mx-auto">
                            {JOURNEY.map((j, i) => (
                                <div key={j} className="flex items-center flex-1" data-testid={`journey-step-${i}`}>
                                    <div className={`flex-1 px-5 py-6 text-center border transition-all duration-500 ${
                                        i === JOURNEY.length - 1
                                            ? "border-[var(--gold)] bg-[rgba(74,14,24,0.45)] shadow-[0_0_40px_rgba(201,162,75,0.18)]"
                                            : "hairline bg-[rgba(38,9,16,0.6)]"
                                    }`}>
                                        <span className="block font-display text-2xl gold-text">0{i + 1}</span>
                                        <span className="block mt-2 text-[0.62rem] tracking-[0.24em] uppercase text-[var(--ivory)]/85">{j}</span>
                                    </div>
                                    {i < JOURNEY.length - 1 && <span className="hidden md:block w-5 h-px shimmer-line shrink-0" />}
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="container-x">
                    <SectionHead eyebrow="What's at Stake" title={<>More Than a Trophy. <span className="gold-text">A Trajectory.</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {PITCH_FEATURES.map((f, i) => (
                            <Reveal key={f.title} delay={i * 70}>
                                <div className="border hairline p-6 h-full hover:border-[var(--line)] hover:bg-[rgba(201,162,75,0.04)] transition-all duration-500">
                                    <f.icon size={20} strokeWidth={1.3} className="text-[var(--gold)]" />
                                    <h3 className="font-display text-lg mt-5 text-[var(--ivory)]">{f.title}</h3>
                                    <p className="mt-2.5 text-xs text-muted-luxe leading-relaxed">{f.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-16 text-center">
                        <p className="font-display italic text-xl text-[var(--champagne)]/85 max-w-2xl mx-auto">
                            "Five rounds. Three days. One stage — and a jury of India's sharpest capital."
                        </p>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <GoldBtn to="/register?type=startup" testid="pitchyatra-cta-bottom">Enter PitchYatra</GoldBtn>
                            <GoldBtn to="/programme" variant="outline" testid="pitchyatra-schedule-btn">See the Schedule</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
